import {
  Form,
  Input,
  Select,
  Button,
  Checkbox,
  Space,
  Tooltip,
  Alert,
  App,
} from "antd";
import { useCallback, useEffect, useMemo, useState } from "react";
import "./userForm.scss";
import { useSchools } from "../../../hooks/useSchools";
import { useGrades } from "../../../hooks/useGrades";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { UserEntityInput } from "../../../services/users/user.types";
import { resolveRoleType } from "./utils";
import {
  computeDefaultPermissions,
  derivePermissionOptions,
} from "./permissions";
import { Role } from "../../../types";
import { handleSubmitFormResponseManage } from "../../../utils";
import {
  createUser,
  updateUser,
  getUserById,
} from "../../../services/users/user.service";
import {
  getCampuses,
  getSchoolById,
  getSchoolUserById,
  setCoordinatorCampusScope,
} from "../../../services/schools/school.service";
import { CampusRef } from "../../../services/schools/campus.types";
import { getClassroomsBySchoolId } from "../../../services/classrooms/classrooms.service";
import { ProgramSchoolAssociation } from "../../../services/schools/school.types";
import ActivityIndicator from "../../ActivityIndicator";
import { LinkOutlined } from "@ant-design/icons";

// SCRUM-1129: username errors the API returns (same rules as SCRUM-836).
const USERNAME_ERRORS: Record<string, string> = {
  "Username is already taken": "Ese usuario ya está en uso.",
  "Username must contain at least 3 valid characters (a-z, 0-9, -, .)":
    "El usuario debe tener al menos 3 caracteres válidos (a-z, 0-9, - y .).",
};

const UserForm = () => {
  const { id: userId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { schools } = useSchools();
  const { grades } = useGrades();
  const [loading, setLoading] = useState<boolean>(false);
  const [enablePasswordEdit, setEnablePasswordEdit] = useState<boolean>(
    !userId
  );
  const [accessUrl, setAccessUrl] = useState<string | undefined>(undefined);
  const [selectedRole, setSelectedRole] = useState<string | undefined>(
    undefined
  );
  const [originalUsername, setOriginalUsername] = useState<string>("");

  // SCRUM-816: catalog of the selected school (classes + licensed programs)
  // feeding "Clases asignadas" and "Permisos de contenido".
  const [selectedSchoolId, setSelectedSchoolId] = useState<number | null>(
    null
  );
  const [mainGradeIds, setMainGradeIds] = useState<number[]>([]);
  const [schoolPrograms, setSchoolPrograms] = useState<
    ProgramSchoolAssociation[]
  >([]);
  const [classroomOptions, setClassroomOptions] = useState<
    { value: number; label: string }[]
  >([]);
  // Once the admin edits the permission selects (or the user already had
  // stored permissions), defaults stop overwriting them.
  // SCRUM-916: planteles del árbol de la escuela elegida. Sólo se cargan para
  // un COORDINATOR — es el único rol que se acota por plantel.
  const [campuses, setCampuses] = useState<CampusRef[]>([]);
  const [permissionsTouched, setPermissionsTouched] = useState<boolean>(false);
  const [defaultsApplied, setDefaultsApplied] = useState<boolean>(false);

  const { notification: api } = App.useApp();

  const [form] = Form.useForm();

  // When creating new user we want showAttendance default to true
  useEffect(() => {
    if (!userId) {
      form.setFieldsValue({ showAttendance: true });
    }
  }, [userId, form]);

  useEffect(() => {
    const fetchInfo = async () => {
      if (userId) {
        setLoading(true);
        try {
          const response = await getUserById(Number(userId));
          if (response) {
            setAccessUrl(response.access_url);
            setSelectedRole(response.role);
            setOriginalUsername(response.username ?? "");
            setSelectedSchoolId(response.school_id ?? null);

            // Extraer solo los IDs de los grados
            const gradeIds = response.grades?.map((g) => g.id) || [];
            const classroomIds = response.classrooms?.map((c) => c.id) || [];
            const visibleGradeIds =
              response.visible_grades?.map((g) => g.id) || [];
            const visibleSubjectIds =
              response.visible_subjects?.map((s) => s.id) || [];

            setMainGradeIds(gradeIds);
            // Stored permissions are the admin's explicit choice: never
            // overwrite them with the computed defaults.
            if (visibleGradeIds.length > 0 || visibleSubjectIds.length > 0) {
              setPermissionsTouched(true);
            }

            form.setFieldsValue({
              ...response,
              password: "",
              grades: gradeIds,
              classroom_ids: classroomIds,
              visible_grades: visibleGradeIds,
              visible_subjects: visibleSubjectIds,
              // ADMIN users don't belong to a school; the API may send
              // null/undefined for school_id in that case, so normalize
              // explicitly instead of letting an undefined value slip
              // through to the Select.
              school_id: response.school_id ?? null,
            });
          }
        } catch (error) {
          api.error({
            message: "Error",
            description:
              "La información no pudo ser cargada, por favor notifica este error!",
            placement: "top",
            duration: 3,
          });
        } finally {
          setLoading(false);
        }
      }
    };

    fetchInfo();
  }, [userId, form, api]);

  // SCRUM-816: load the selected school's classes and licensed programs.
  useEffect(() => {
    const fetchSchoolCatalog = async () => {
      if (!selectedSchoolId || selectedRole !== "TEACHER") {
        setSchoolPrograms([]);
        setClassroomOptions([]);
        return;
      }
      try {
        const [school, classrooms] = await Promise.all([
          getSchoolById(selectedSchoolId),
          getClassroomsBySchoolId(selectedSchoolId),
        ]);
        setSchoolPrograms(school?.programs ?? []);
        setClassroomOptions(
          (classrooms ?? []).map((classroom) => ({
            value: classroom.id,
            label: classroom.custom_code,
          }))
        );
      } catch (error) {
        api.error({
          message: "Error",
          description:
            "No se pudieron cargar las clases y programas de la escuela.",
          placement: "top",
          duration: 3,
        });
      }
    };

    fetchSchoolCatalog();
  }, [selectedSchoolId, selectedRole, api]);

  // SCRUM-916: el alcance por plantel se lee de GET /schools/users/:id, que es
  // el único endpoint que devuelve campus_scope_ids (GET /users/:id no).
  useEffect(() => {
    const fetchCampusScope = async () => {
      if (!selectedSchoolId || selectedRole !== "COORDINATOR") {
        setCampuses([]);
        return;
      }
      try {
        const tree = (await getCampuses(selectedSchoolId)) ?? [];
        setCampuses(tree);
        if (userId && tree.length > 1) {
          const detail = await getSchoolUserById(Number(userId));
          form.setFieldsValue({
            campus_scope_ids: detail?.campus_scope_ids ?? [],
          });
        }
      } catch (error) {
        setCampuses([]);
      }
    };

    fetchCampusScope();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSchoolId, selectedRole, userId]);

  const { gradeOptions: visibleGradeOptions, subjectOptions } = useMemo(
    () => derivePermissionOptions(schoolPrograms),
    [schoolPrograms]
  );

  // SCRUM-816: preselect the default permissions (the section of the main
  // grade + its subjects) until the admin edits them by hand.
  useEffect(() => {
    if (selectedRole !== "TEACHER" || permissionsTouched) return;
    if (schoolPrograms.length === 0) return;
    const defaults = computeDefaultPermissions(schoolPrograms, mainGradeIds);
    form.setFieldsValue({
      visible_grades: defaults.grades,
      visible_subjects: defaults.subjects,
    });
    setDefaultsApplied(true);
  }, [schoolPrograms, mainGradeIds, selectedRole, permissionsTouched, form]);

  const handleSubmit = async (values: UserEntityInput) => {
    const submittedValues = { ...values };

    if (submittedValues.grades && !Array.isArray(submittedValues.grades)) {
      submittedValues.grades = [submittedValues.grades];
    }

    // Si estamos editando y no se habilitó el campo de contraseña, eliminar el campo password
    if (userId && !enablePasswordEdit) {
      delete submittedValues.password;
    }

    // Si estamos creando (no editando), eliminar is_active (el backend lo asigna por defecto)
    if (!userId) {
      delete submittedValues.is_active;
    }

    // SCRUM-1129: la mayoría de los alumnos no tiene correo. Un campo vacío se
    // manda como null porque la API rechaza "" como correo inválido.
    if (
      userId &&
      submittedValues.role === "STUDENT" &&
      !submittedValues.email?.trim()
    ) {
      submittedValues.email = null;
    }

    // El username solo viaja si el admin lo cambió: reenviar uno viejo con
    // mayúsculas haría que la API lo normalice sin que nadie lo pidiera.
    if (
      !userId ||
      submittedValues.role !== "STUDENT" ||
      (submittedValues.username ?? "").trim() === originalUsername
    ) {
      delete submittedValues.username;
    }

    // ADMIN users don't belong to a school: make sure a stale school_id
    // value doesn't leak through if the field was visible before the role
    // was switched to ADMIN.
    if (submittedValues.role === "ADMIN") {
      submittedValues.school_id = null;
    }

    // SCRUM-816: assigned classes and content permissions only apply to
    // teachers — the fields are only mounted for that role, but a stale
    // value could survive a role switch.
    if (submittedValues.role !== "TEACHER") {
      delete submittedValues.classroom_ids;
      delete submittedValues.visible_grades;
      delete submittedValues.visible_subjects;
    }

    // SCRUM-916: el alcance por plantel viaja por su propio endpoint, nunca
    // dentro del payload de usuario — así el DTO de usuario no crece y el
    // orden de validación queda explícito. Sólo se manda si el campo estuvo
    // montado: si no, un array vacío borraría un alcance ya configurado.
    const sendsCampusScope =
      submittedValues.role === "COORDINATOR" && campuses.length > 1;
    const campusScopeIds = sendsCampusScope
      ? (submittedValues.campus_scope_ids ?? [])
      : null;
    delete submittedValues.campus_scope_ids;

    const createUserWithCampusScope = async (values: UserEntityInput) => {
      const response = await createUser(values);
      if (response?.id && campusScopeIds) {
        await setCoordinatorCampusScope(response.id, campusScopeIds);
      }
      return response;
    };

    const updateUserWithCampusScope = async (
      id: number,
      values: UserEntityInput
    ) => {
      const response = await updateUser(id, values);
      if (response?.id && campusScopeIds) {
        await setCoordinatorCampusScope(id, campusScopeIds);
      }
      return response;
    };

    // SCRUM-816: los permisos sugeridos que el admin no tocó no se guardan.
    // El formulario los propone (la sección del grado principal) para que se
    // vea qué implica el comportamiento por defecto, pero proponerlos no es
    // decidirlos: enviarlos escribiría una restricción explícita que nadie
    // configuró — bastaba abrir un profesor para corregirle el teléfono y
    // guardar — y esa restricción quedaría congelada el día que cambie la
    // regla por defecto. Sin enviarlos el backend deja el campo intacto
    // (`if (visible_grades !== undefined)`), y el texto de la sección pasa a
    // ser cierto: sin selección propia, el usuario conserva su comportamiento.
    if (defaultsApplied && !permissionsTouched) {
      delete submittedValues.visible_grades;
      delete submittedValues.visible_subjects;
    }

    await handleSubmitFormResponseManage(
      submittedValues,
      createUserWithCampusScope,
      updateUserWithCampusScope,
      navigate,
      dispatch,
      "user",
      "addUser",
      "updateUser",
      Number(userId),
      (error: any) => {
        const usernameError = USERNAME_ERRORS[error?.response?.data?.message];
        if (usernameError) {
          form.setFields([{ name: "username", errors: [usernameError] }]);
          return;
        }
        if (error?.response?.data?.message) {
          api.error({
            message: "Error al guardar usuario",
            description: `${error.response.data.message}\n\nDebes dejarle o asignarle el grado que marca el error para poder continuar.`,
            placement: "top",
            duration: 8,
            style: { whiteSpace: "pre-line" },
          });
        } else {
          api.error({
            message: "Error al guardar usuario",
            description:
              error?.message ||
              "Ocurrió un error inesperado al guardar el usuario.",
            placement: "top",
            duration: 5,
          });
        }
      }
    );
  };

  const handleRoleChange = (role: string) => {
    setSelectedRole(role);

    // Si se cambia a STUDENT y hay múltiples grados, mantener solo el primero
    if (role === "STUDENT") {
      const currentGrades = form.getFieldValue("grades") || [];
      if (currentGrades.length > 1) {
        form.setFieldsValue({ grades: [currentGrades[0]] });
      }
    }
  };

  // SCRUM-816: another school means other classes and another catalog —
  // drop the selection and recompute the defaults from scratch.
  const handleSchoolChange = useCallback(
    (schoolId: number | null) => {
      setSelectedSchoolId(schoolId ?? null);
      // SCRUM-916: otra escuela es otro árbol de planteles — un alcance viejo
      // apuntaría a ids que el backend rechaza con 400.
      form.setFieldsValue({ classroom_ids: [], campus_scope_ids: [] });
      setPermissionsTouched(false);
      setDefaultsApplied(false);
      form.setFieldsValue({ visible_grades: [], visible_subjects: [] });
    },
    [form]
  );

  const handleOpenAccessUrl = () => {
    if (accessUrl) {
      window.open(accessUrl, "_blank", "noopener,noreferrer");
    }
  };

  if (loading) {
    return <ActivityIndicator />;
  }

  const isTeacher = selectedRole === "TEACHER";
  // SCRUM-916: con un solo plantel el campo no tiene nada que elegir, así que
  // en 147 de 148 escuelas el formulario queda idéntico.
  const showCampusScope =
    selectedRole === "COORDINATOR" && campuses.length > 1;

  return (
    <>
      <Form
        layout="vertical"
        form={form}
        onFinish={handleSubmit}
        className="user-form-container"
        initialValues={{ is_active: true, showAttendance: true }}
        onValuesChange={(changedValues) => {
          // Detectar cambios en el campo 'role' para actualizar selectedRole
          if (changedValues.role !== undefined) {
            handleRoleChange(changedValues.role);
          }
          if (changedValues.school_id !== undefined) {
            handleSchoolChange(changedValues.school_id);
          }
          if (changedValues.grades !== undefined) {
            const value = changedValues.grades;
            setMainGradeIds(
              Array.isArray(value) ? value : value != null ? [value] : []
            );
          }
          // A manual edit of the permission selects stops the defaults from
          // overwriting the admin's choice.
          if (
            changedValues.visible_grades !== undefined ||
            changedValues.visible_subjects !== undefined
          ) {
            setPermissionsTouched(true);
            setDefaultsApplied(false);
          }
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "20px",
          }}
        >
          {userId ? <h2>Editar usuario</h2> : <h2>Crear usuario</h2>}
          {userId && accessUrl && (
            <Tooltip title="Abrir web con el usuario">
              <Button
                type="default"
                icon={<LinkOutlined />}
                onClick={handleOpenAccessUrl}
                style={{
                  backgroundColor: "#52c41a",
                  borderColor: "#52c41a",
                  color: "white",
                }}
              >
                Abrir Web
              </Button>
            </Tooltip>
          )}
        </div>

        <h3 className="user-form-section-title">Información</h3>

        <Form.Item
          label="Nombre"
          name="first_name"
          rules={[{ required: true, message: "Por favor ingresa el nombre" }]}
        >
          <Input placeholder="Juan" />
        </Form.Item>

        <Form.Item
          label="Apellido"
          name="last_name"
          rules={[{ required: true, message: "Por favor ingresa el apellido" }]}
        >
          <Input placeholder="Pérez" />
        </Form.Item>

        <Form.Item
          label="Correo electrónico"
          name="email"
          rules={[
            {
              required: !(userId && selectedRole === "STUDENT"),
              message: "Por favor ingresa el correo",
            },
            { type: "email", message: "Correo inválido" },
          ]}
        >
          <Input placeholder="usuario@correo.com" />
        </Form.Item>

        {userId && selectedRole === "STUDENT" && (
          <Form.Item
            label="Usuario"
            name="username"
            extra="Se guarda en minúsculas, sin acentos ni espacios."
            rules={[
              {
                required: Boolean(originalUsername),
                whitespace: true,
                message: "Por favor ingresa el usuario",
              },
              {
                max: 64,
                message: "El usuario puede tener hasta 64 caracteres",
              },
            ]}
          >
            <Input placeholder="nombre.apellido" />
          </Form.Item>
        )}

        {userId && (
          <Form.Item>
            <Space>
              <Checkbox
                checked={enablePasswordEdit}
                onChange={(e) => setEnablePasswordEdit(e.target.checked)}
              >
                Cambiar contraseña
              </Checkbox>
              {!enablePasswordEdit && (
                <span style={{ color: "#999", fontSize: "12px" }}>
                  (La contraseña se podrá modificar cuando actives este
                  checkbox)
                </span>
              )}
            </Space>
          </Form.Item>
        )}

        <Form.Item
          label="Contraseña"
          name="password"
          rules={[
            {
              required: !userId || enablePasswordEdit,
              message: "Por favor ingresa la contraseña",
            },
          ]}
        >
          <Input.Password
            placeholder="••••••••"
            disabled={userId ? !enablePasswordEdit : false}
          />
        </Form.Item>

        <Form.Item
          label="Rol"
          name="role"
          rules={[{ required: true, message: "Selecciona un rol" }]}
        >
          <Select
            placeholder="Selecciona un rol"
            options={Object.values(Role).map((type) => ({
              value: type,
              label: resolveRoleType(type),
            }))}
          />
        </Form.Item>

        {selectedRole && selectedRole !== "ADMIN" && (
          <Form.Item
            label="Escuela"
            name="school_id"
            rules={[{ required: true, message: "Selecciona una escuela" }]}
          >
            <Select
              placeholder="Selecciona una escuela"
              options={schools.map((school) => ({
                value: school.id,
                label: school.name,
              }))}
            />
          </Form.Item>
        )}

        {selectedRole && selectedRole !== "ADMIN" && (
          <Form.Item
            label={selectedRole === "STUDENT" ? "Grado" : "Grado principal"}
            name="grades"
            tooltip={
              selectedRole === "STUDENT"
                ? undefined
                : "Grado principal al que pertenece el usuario"
            }
            rules={[
              {
                required: true,
                message: "Selecciona al menos un grado",
              },
            ]}
          >
            <Select
              mode={selectedRole === "STUDENT" ? undefined : "multiple"}
              placeholder={
                selectedRole === "STUDENT"
                  ? "Seleccionar grado"
                  : "Seleccionar grados"
              }
              maxTagCount={selectedRole === "STUDENT" ? undefined : 2}
              maxTagTextLength={10}
              options={grades.map(
                (grade: { id: number; code: string; grade: string }) => ({
                  value: grade.id,
                  label: grade.code,
                })
              )}
            />
          </Form.Item>
        )}

        {showCampusScope && (
          <>
            <h3 className="user-form-section-title">Planteles</h3>
            <p className="user-form-section-hint">
              Acota qué planteles administra este coordinador. Vacío = ve todos
              los planteles de la escuela.
            </p>
            <Form.Item label="Planteles" name="campus_scope_ids">
              <Select
                mode="multiple"
                placeholder="Seleccionar planteles"
                options={campuses.map((campus) => ({
                  value: campus.id,
                  label: campus.name,
                }))}
              />
            </Form.Item>
          </>
        )}

        {isTeacher && (
          <>
            <h3 className="user-form-section-title">Clases asignadas</h3>
            <p className="user-form-section-hint">
              Selecciona las clases o grupos que imparte este usuario. Podrá
              ver y gestionar únicamente estas clases y sus alumnos.
            </p>
            <Form.Item label="Clases" name="classroom_ids">
              <Select
                mode="multiple"
                placeholder="Seleccionar clases"
                maxTagCount={4}
                disabled={!selectedSchoolId}
                options={classroomOptions}
              />
            </Form.Item>

            <h3 className="user-form-section-title">Permisos de contenido</h3>
            <p className="user-form-section-hint">
              Define qué grados y asignaturas puede consultar este usuario en
              la plataforma. Sin una selección propia, el usuario conserva el
              comportamiento por defecto: los grados de su sección y las
              materias que dicta.
            </p>
            <Form.Item label="Grados que puede ver" name="visible_grades">
              <Select
                mode="multiple"
                placeholder="Seleccionar grados que puede ver"
                disabled={!selectedSchoolId}
                options={visibleGradeOptions}
              />
            </Form.Item>
            <Form.Item
              label="Asignaturas que puede ver"
              name="visible_subjects"
            >
              <Select
                mode="multiple"
                placeholder="Seleccionar asignaturas que puede ver"
                disabled={!selectedSchoolId}
                options={subjectOptions}
              />
            </Form.Item>
            {defaultsApplied && (
              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
                message="Permisos por defecto sugeridos"
                description="Es lo que este usuario ve hoy: los grados de la sección de su grado principal y sus materias. Si no modificas la selección no se guarda ninguna restricción. Modifícala sólo si necesitas acotarlo más."
              />
            )}
          </>
        )}

        {(isTeacher || userId) && (
          <h3 className="user-form-section-title">Configuración</h3>
        )}

        {isTeacher && (
          <Form.Item name="showAttendance" valuePropName="checked">
            <Checkbox>Mostrar asistencias</Checkbox>
          </Form.Item>
        )}

        {userId && (
          <Form.Item name="is_active" valuePropName="checked">
            <Checkbox>Usuario activo</Checkbox>
          </Form.Item>
        )}

        <Form.Item>
          <Button type="primary" htmlType="submit" block>
            Guardar usuario
          </Button>
        </Form.Item>
      </Form>
    </>
  );
};

export default UserForm;
